/**
 * Collection page functionality
 * Handles category filtering and enhanced user interactions
 */

class CollectionPage extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    this.setupAccessibility();
    this.setupKeyboardNavigation();
    this.setupLoadingStates();
  }

  /**
   * Enhance accessibility for collection elements
   */
  setupAccessibility() {
    // Ensure proper focus management for accordion
    const accordionSummary = this.querySelector(
      ".collection-categories-accordion__summary"
    );
    const accordion = this.querySelector(".collection-categories-accordion");

    if (accordionSummary && accordion) {
      accordionSummary.addEventListener("click", () => {
        // Update aria-expanded attribute
        const isOpen = accordion.hasAttribute("open");
        accordionSummary.setAttribute("aria-expanded", !isOpen);
      });

      // Set initial aria-expanded state
      const isInitiallyOpen = accordion.hasAttribute("open");
      accordionSummary.setAttribute("aria-expanded", isInitiallyOpen);
    }
  }

  /**
   * Setup keyboard navigation for category links
   */
  setupKeyboardNavigation() {
    const categoryLinks = this.querySelectorAll(".collection-categories__link");

    categoryLinks.forEach((link) => {
      link.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          link.click();
        }
      });
    });
  }

  /**
   * Add loading states for better UX
   */
  setupLoadingStates() {
    const categoryLinks = this.querySelectorAll(".collection-categories__link");

    categoryLinks.forEach((link) => {
      link.addEventListener("click", (e) => {
        // Don't add loading state if it's the current page
        if (link.getAttribute("aria-current") === "page") {
          e.preventDefault();
          return;
        }

        // Add loading state
        link.classList.add("collection-categories__link--loading");

        // Optional: Add a small delay to show the loading state
        setTimeout(() => {
          window.location.href = link.href;
        }, 100);
      });
    });
  }
}

// Initialize when DOM is loaded
if (!customElements.get("collection-page")) {
  customElements.define("collection-page", CollectionPage);
}

/**
 * Product card hover enhancements
 */
class ProductCard extends HTMLElement {
  constructor() {
    super();
    this.setupHoverEffects();
  }

  setupHoverEffects() {
    const link = this.querySelector(".product-card__link");

    if (link) {
      // Add smooth focus transitions
      link.addEventListener("focusin", () => {
        this.classList.add("product-card--focused");
      });

      link.addEventListener("focusout", () => {
        this.classList.remove("product-card--focused");
      });
    }
  }
}

if (!customElements.get("product-card")) {
  customElements.define("product-card", ProductCard);
}

/**
 * Utility function for smooth scrolling to elements
 */
const smoothScrollTo = (element, offset = 0) => {
  const elementPosition =
    element.getBoundingClientRect().top + window.pageYOffset;
  const offsetPosition = elementPosition - offset;

  window.scrollTo({
    top: offsetPosition,
    behavior: "smooth",
  });
};

// Export for use in other scripts
window.CollectionPageUtils = {
  smoothScrollTo,
};
